import { FormatMessageType } from '@hooks/useValidation';
import { booleanValid, kinks } from '@turf/turf';
import { REQUIRED } from '@utils/validation/validation.constant';
import Yup from '@utils/validation/yup-extended';
import { MultiPolygon, Polygon, Position } from 'geojson';

function validatePosition(formatMessage: FormatMessageType, position: Position) {
  if (position[0] < -180 || 180 < position[0]) {
    return formatMessage({ id: 'form.longitude' });
  }
  if (position[1] < -90 || 90 < position[1]) {
    return formatMessage({ id: 'form.latitude' });
  }
}

function validateRings(formatMessage: FormatMessageType, geometry: Polygon | MultiPolygon) {
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;

  for (const polygon of polygons) {
    for (const ring of polygon) {
      if (ring.length < 4) {
        return formatMessage({ id: 'form.polygon.ringMinPoints' });
      }

      const first = ring.at(0);
      const last = ring.at(-1);
      if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
        return formatMessage({ id: 'form.polygon.closedRing' });
      }

      for (const position of ring) {
        validatePosition(formatMessage, position);
      }
    }
  }

  return null;
}

export const polygonSchema = (formatMessage: FormatMessageType) =>
  Yup.object({
    type: Yup.string()
      .oneOf(['Polygon', 'MultiPolygon'])
      .required(formatMessage({ id: REQUIRED })),
    coordinates: Yup.array().required(formatMessage({ id: REQUIRED })),
  })
    .required(formatMessage({ id: REQUIRED }))
    .test('valid-geometry', function (geometry) {
      if (geometry.coordinates.length === 0) {
        return this.createError({ message: formatMessage({ id: 'form.polygon.emptyCoordinates' }) });
      }

      const ringError = validateRings(formatMessage, geometry);
      if (ringError) {
        return this.createError({ message: ringError });
      }

      if (!booleanValid(geometry)) {
        return this.createError({ message: formatMessage({ id: 'form.polygon.invalidGeometry' }) });
      }

      const intersections = kinks(geometry);
      if (intersections.features.length > 0) {
        const intersectionPoints = intersections.features.map((feature) => feature.geometry.coordinates);

        return this.createError({
          message: formatMessage(
            { id: 'form.polygon.selfIntersections' },
            {
              count: intersectionPoints.length,
              coordinates: intersectionPoints.map((coords) => `[${coords.join(', ')}]`).join(', '),
            },
          ),
        });
      }

      return true;
    });
